import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Switch from "@mui/material/Switch";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import curved9 from "assets/images/preview.png";
import toast from "react-hot-toast";
import { baseURL } from "utils/api";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";

function SignIn() {
	const userTheme = useSelector((state) => state.commonSlice.theme);
	const [rememberMe, setRememberMe] = useState(true);
	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [errors, setErrors] = useState({
		email: '',
		password: ''
	});
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = useState(false);

	useEffect(() => {
		const savedEmail = localStorage.getItem("rememberMeEmail") || false;
		const savedPassword = localStorage.getItem("rememberMePassword") || false;
		
		if (savedEmail && savedPassword) {
			setEmail(savedEmail);
			setPassword(savedPassword);
			setRememberMe(true);
		}
	}, []);

	const validateEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const validatePassword = (password) => {
		return password.length >= 6;
	};

	const validateForm = () => {
		const newErrors = {
			email: '',
			password: ''
		};

		if (!email) {
			newErrors.email = 'Email is required';
		} else if (!validateEmail(email)) {
			newErrors.email = 'Please enter a valid email address';
		}

		if (!password) {
			newErrors.password = 'Password is required';
		} else if (!validatePassword(password)) {
			newErrors.password = 'Password must be at least 6 characters long';
		}

		setErrors(newErrors);
		return !newErrors.email && !newErrors.password;
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		if (rememberMe) {
			
			localStorage.setItem("rememberMeEmail", email);
			localStorage.setItem("rememberMePassword", password);
		} else {
			localStorage.removeItem("rememberMeEmail");
			localStorage.removeItem("rememberMePassword");
		}

		setLoading(true);
		var query= {email, password};
		if (email == "admin@1onus.com" || email == "marketingteam@yopmail.com"){
			query.role = "ADMIN";
		}else{
			query.role = "BUSINESS";
		}
   console.log(query,'query');

		fetch(`${baseURL}login`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(query),
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.success) {
					toast.success(data.message);
					localStorage.setItem("token", data?.token);
					localStorage.setItem("info", JSON.stringify(data?.data));
					localStorage.setItem("id", data?.data?._id);
					localStorage.setItem("scope", `${data?.data?.role === 'ADMIN' ? '1' : '2'}`);
					localStorage.setItem("clickedItem", 'dashboard')
					navigate(`${data?.data?.role === 'ADMIN' ? "/admin/dashboard" : "/dashboard"}`);
				} else {
					toast.error(data.message);
				}
			})
			.catch((err) => {
				toast.error(err.message || 'Something went wrong');;
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handlePasswordToggle = () => {
		setShowPassword((prev) => !prev);
	};

	return (
		<CoverLayout
			title="Welcome back"
			description="Enter your email and password to sign in"
			image={curved9}
			forgot={false}
		>
			<SoftBox>
				<SoftBox component="form" role="form" onSubmit={handleSubmit}>
					<SoftBox mb={2}>
						<SoftBox mb={1} ml={0.5}>
							<SoftTypography component="label" variant="caption" fontWeight="bold">
								Email
							</SoftTypography>
						</SoftBox>
						<SoftInput
							name="email"
							value={email}
							onChange={(e) => {
								setEmail(e.target.value);
								setErrors(prev => ({ ...prev, email: '' }));
							}}
							type="email"
							placeholder="Email"
							error={!!errors.email}
							sx={{
								border: errors.email ? '1px solid #ef5350' : '1px solid #d1d5db',
								borderRadius: '6px',
								padding: '10px 12px',

								'&:hover': {
									borderColor: errors.email ? '#ef5350' : '#9ca3af',
								},

								'&.Mui-focused': {
									borderColor: errors.email ? '#ef5350' : '#3b82f6',
									boxShadow: errors.email ? '0 0 0 1px rgba(239, 83, 80, 0.2)' : '0 0 0 1px rgba(59, 130, 246, 0.2)',
								},

								'& input': {
									padding: 0,
									height: 'auto',
								},

								'& input:-webkit-autofill': {
									WebkitBoxShadow: '0 0 0 1000px white inset',
									WebkitTextFillColor: '#000',
								},
							}}
						/>
						{errors.email && (
							<SoftTypography
								variant="caption"
								sx={{ 
									color: '#ef5350'
								}}
							>
								{errors.email}
							</SoftTypography>
						)}
					</SoftBox>
					<SoftBox mb={2}>
						<SoftBox mb={1} ml={0.5}>
							<SoftTypography component="label" variant="caption" fontWeight="bold">
								Password
							</SoftTypography>
						</SoftBox>
						<div style={{ position: "relative" }}>
							<SoftInput
								name="password"
								value={password}
								onChange={(e) => {
									setPassword(e.target.value);
									setErrors(prev => ({ ...prev, password: '' }));
								}}
								type={showPassword ? "text" : "password"}
								placeholder="Password"
								error={!!errors.password}
								sx={{
									border: errors.password ? '1px solid #ef5350' : '1px solid #d1d5db',
									borderRadius: '6px',
									padding: '10px 12px',

									'&:hover': {
										borderColor: errors.password ? '#ef5350' : '#9ca3af',
									},

									'&.Mui-focused': {
										borderColor: errors.password ? '#ef5350' : '#3b82f6',
										boxShadow: errors.password ? '0 0 0 1px rgba(239, 83, 80, 0.2)' : '0 0 0 1px rgba(59, 130, 246, 0.2)',
									},

									'& input': {
										padding: 0,
										height: 'auto',
									},

									'& input:-webkit-autofill': {
										WebkitBoxShadow: '0 0 0 1000px white inset',
										WebkitTextFillColor: '#000',
									},
								}}
							/>
							<div
								onClick={handlePasswordToggle}
								style={{
									position: "absolute",
									right: "10px",
									top: "60%",
									transform: "translateY(-50%)",
									cursor: "pointer",
									color: userTheme === 'dark' ? 'white' : 'black'
								}}
							>
								{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
							</div>
						</div>
						{errors.password && (
							<SoftTypography
								variant="caption"
								sx={{ 
									color: '#ef5350'
								}}
							>
								{errors.password}
							</SoftTypography>
						)}
					</SoftBox>
					<SoftBox mb={2} display="flex" justifyContent="space-between">
						<SoftBox display="flex" alignItems="center">

							<Switch checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} />
							<SoftTypography
								variant="button"
								fontWeight="regular"
								onClick={() => setRememberMe(!rememberMe)}
								sx={{ cursor: "pointer", userSelect: "none" }}
							>
								&nbsp;&nbsp;Remember me
							</SoftTypography>
						</SoftBox>
						<SoftTypography
							component={Link}
							to="/forgot-password"
							variant="button"
							color="info"
							fontWeight="medium"
							textGradient
						>
							Forgot password?
						</SoftTypography>
					</SoftBox>
					<SoftBox mt={4} mb={1}></SoftBox>
					<SoftButton 
						type="submit" 
						variant="gradient" 
						color="info" 
						fullWidth
						disabled={loading}
					>
						{loading ? (
							<CircularProgress size={24} color="inherit" />
						) : (
							"sign in"
						)}
					</SoftButton>
				</SoftBox>
				<SoftBox mt={3} textAlign="center">
					<SoftTypography variant="button"
						sx={{
							color: userTheme === 'dark' ? 'white' : 'black'
						}}
						fontWeight="regular"> Don&apos;t have an account?{" "}</SoftTypography>
					<SoftTypography
						component={Link}
						to="/register"
						variant="button"
						color="info"
						fontWeight="medium"
						textGradient
					>
						Sign up
					</SoftTypography>
				</SoftBox>
			</SoftBox>
		</CoverLayout>
	);
}

export default SignIn;
